import React from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';
import {
  Button,
  DisplayText,
  MobileHeader as GarnishMobileHeader,
  theme,
  useBreakpointHelpers,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import type { FooterProps, HeaderProps } from '../CreditCardForm.types';

export const LineItem = ({
  style,
  children,
}: Pick<ViewProps, 'style' | 'children'>) => {
  const { currentBreakpoint } = useBreakpointHelpers();
  const paddingHorizontal = currentBreakpoint.isMobile ? 16 : 24;
  const dynamic = useStyle(() => ({ paddingHorizontal }), [paddingHorizontal]);

  return <View style={[styles.lineItem, dynamic, style]}>{children}</View>;
};

export const Header = (props: HeaderProps) => {
  const { currentBreakpoint } = useBreakpointHelpers();
  const Responsive = currentBreakpoint.isMobile ? MobileHeader : DesktopHeader;

  return <Responsive {...props} />;
};

export const Footer = ({
  mode,
  isSaving,
  isDeleting,
  canSubmit,
  onDelete,
  onSubmit,
  footerFullWidth,
}: FooterProps) => {
  const { t } = useLocalizationContext();
  const isEditing = mode === 'edit';
  const paddingHorizontal = useHorizontalPadding(isEditing);
  const style = useStyle(() => ({ paddingHorizontal }), [paddingHorizontal]);

  const saveButtonWidth = footerFullWidth
    ? '100%'
    : isEditing
    ? '48.5%'
    : '100%';

  return (
    <View style={[styles.row, footerFullWidth ? {} : style]}>
      {isEditing ? (
        <Button
          testID="account.credit-card-form.remove"
          size="large"
          palette="caution"
          width="48.5%"
          isLoading={isDeleting}
          accessibilityRole="button"
          accessibilityLabel={t('account.credit-card-form.remove')}
          accessibilityHint={t('account.credit-card-form.remove')}
          onPress={onDelete}
        >
          {t('account.credit-card-form.remove')}
        </Button>
      ) : null}
      <Button
        testID="account.credit-card-form.submit"
        size="large"
        palette="primary"
        width={saveButtonWidth}
        isLoading={isSaving}
        disabled={!canSubmit}
        accessibilityRole="button"
        accessibilityLabel={t('account.credit-card-form.save')}
        accessibilityHint={t('account.credit-card-form.save')}
        accessibilityState={{ disabled: !canSubmit }}
        onPress={onSubmit}
      >
        {t('account.credit-card-form.save')}
      </Button>
    </View>
  );
};

const DesktopHeader = ({ mode, ...rest }: HeaderProps) => {
  const { t } = useLocalizationContext();
  const title = rest.title ?? t(`account.credit-card-form.${mode}`);

  return (
    <View style={styles.desktopHeader}>
      <DisplayText size={3}>{title}</DisplayText>
    </View>
  );
};

const MobileHeader = ({ mode, subtitle, ...rest }: HeaderProps) => {
  const { t } = useLocalizationContext();
  const title = rest.title ?? t(`account.credit-card-form.${mode}`);

  return (
    <View style={styles.mobileHeaderPadding}>
      <GarnishMobileHeader title={title} subtitle={subtitle} />
    </View>
  );
};

const useHorizontalPadding = (isEditing?: boolean): number => {
  const { currentBreakpoint } = useBreakpointHelpers();

  if (currentBreakpoint.isMobile) return theme.spacing.medium;

  if (isEditing) return theme.spacing.xlarge;

  return theme.spacing.xxlarge;
};

const styles = StyleSheet.create({
  desktopHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: theme.spacing.large,
    paddingHorizontal: theme.spacing.medium,
  },
  mobileHeaderPadding: {
    paddingHorizontal: theme.spacing.medium,
  },
  lineItem: {
    marginBottom: theme.spacing.large,
  },
  row: {
    paddingVertical: theme.spacing.large,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
});
